import { PaymentEnums } from "models/enums";
import { TranslationKeys } from "translations";
import { WintSuperkollAPIModel } from "wint-api-client/dist/types";
import EnumHelper from "./enumHelper";

const PaymentHelper = {
  getCategoryName: (type: PaymentEnums.BankPaymentType) => {
    switch (type) {
      case PaymentEnums.BankPaymentType.IncomingInvoiceManualPayment: {
        return TranslationKeys.Payments.IncomingInvoiceShortName;
      }
      case PaymentEnums.BankPaymentType.IncomingInvoicePaymentFile: {
        return TranslationKeys.Payments.IncomingInvoicePaymentFiles;
      }
      case PaymentEnums.BankPaymentType.ReceiptPayoutFile: {
        return TranslationKeys.Common.Receipts;
      }
      case PaymentEnums.BankPaymentType.SalaryPayout: {
        return TranslationKeys.Payments.SalaryPayouts;
      }
      case PaymentEnums.BankPaymentType.ManualPayout: {
        return TranslationKeys.Payments.ManualPayouts;
      }
      default: {
        return "";
      }
    }
  },

  getPaymentsByType: (type: PaymentEnums.BankPaymentType, payments: WintSuperkollAPIModel.BankPaymentInformation) => {
    switch (type) {
      case PaymentEnums.BankPaymentType.IncomingInvoiceManualPayment: {
        return payments.incomingInvoiceManualPayments;
      }
      case PaymentEnums.BankPaymentType.IncomingInvoicePaymentFile: {
        return payments.incomingInvoicePaymentFilesToSign;
      }
      case PaymentEnums.BankPaymentType.ReceiptPayoutFile: {
        return payments.receiptPayoutFilesToSign;
      }
      case PaymentEnums.BankPaymentType.SalaryPayout: {
        return payments.salaryPayoutFilesToSign;
      }
      case PaymentEnums.BankPaymentType.ManualPayout: {
        return payments.manualPayoutsToSign;
      }
      default: {
        return [];
      }
    }
  },

  updatePaymentsByType: (
    type: PaymentEnums.BankPaymentType,
    payments: WintSuperkollAPIModel.BankPaymentInformation,
    value: any
  ) => {
    switch (type) {
      case PaymentEnums.BankPaymentType.IncomingInvoiceManualPayment: {
        return { ...payments, incomingInvoiceManualPayments: value };
      }
      case PaymentEnums.BankPaymentType.IncomingInvoicePaymentFile: {
        return { ...payments, incomingInvoicePaymentFilesToSign: value };
      }
      case PaymentEnums.BankPaymentType.ReceiptPayoutFile: {
        return { ...payments, receiptPayoutFilesToSign: value };
      }
      case PaymentEnums.BankPaymentType.SalaryPayout: {
        return { ...payments, salaryPayoutFilesToSign: value };
      }
      case PaymentEnums.BankPaymentType.ManualPayout: {
        return { ...payments, manualPayoutsToSign: value };
      }
      default: {
        return payments;
      }
    }
  },

  calculateTotal: (payments: WintSuperkollAPIModel.BankPaymentInformation) => {
    let total = 0;
    EnumHelper.getNumericItemValues(PaymentEnums.BankPaymentType).forEach((key) => {
      total +=
        PaymentHelper.getPaymentsByType(key, payments)
          ?.map((p) => p.amount?.value ?? 0)
          .reduce((a, b) => a + b, 0) ?? 0;
    });
    return total;
  },

  getTotalPaymentsCount: (payments: WintSuperkollAPIModel.BankPaymentInformation) => {
    return (
      (payments.incomingInvoiceManualPayments?.length ?? 0) +
      (payments.incomingInvoicePaymentFilesToSign?.flatMap((f) => f.incomingInvoicePayments)?.length ?? 0) +
      (payments.receiptPayoutFilesToSign?.flatMap((r) => r.receipts).length ?? 0) +
      (payments.salaryPayoutFilesToSign?.length ?? 0) +
      (payments.manualPayoutsToSign?.length ?? 0)
    );
  },

  getBankIdPaymentsByType: (
    type: PaymentEnums.BankIdPaymentType,
    payments: WintSuperkollAPIModel.PaymentSigningInformation
  ) => {
    switch (type) {
      case PaymentEnums.BankIdPaymentType.IncomingInvoice: {
        return payments.incomingInvoices;
      }
      case PaymentEnums.BankIdPaymentType.Receipt: {
        return payments.receipts;
      }
      case PaymentEnums.BankIdPaymentType.SalaryPayout: {
        return payments.salaryPayoutFileRows;
      }
      case PaymentEnums.BankIdPaymentType.ManualPayout: {
        return payments.manualPayouts;
      }
      case PaymentEnums.BankIdPaymentType.PersonSalaryEntry: {
        return payments.personSalaryEntries;
      }
      case PaymentEnums.BankIdPaymentType.WintSalaryEntry: {
        return payments.wintSalaryEntries;
      }
      default: {
        return [];
      }
    }
  },

  updateBankIdPaymentsByType: (
    type: PaymentEnums.BankIdPaymentType,
    payments: WintSuperkollAPIModel.PaymentSigningInformation,
    value: any
  ) => {
    switch (type) {
      case PaymentEnums.BankIdPaymentType.IncomingInvoice: {
        return { ...payments, incomingInvoices: value };
      }
      case PaymentEnums.BankIdPaymentType.Receipt: {
        return { ...payments, receipts: value };
      }
      case PaymentEnums.BankIdPaymentType.SalaryPayout: {
        return { ...payments, salaryPayoutFileRows: value };
      }
      case PaymentEnums.BankIdPaymentType.ManualPayout: {
        return { ...payments, manualPayouts: value };
      }
      case PaymentEnums.BankIdPaymentType.PersonSalaryEntry: {
        return { ...payments, personSalaryEntries: value };
      }
      case PaymentEnums.BankIdPaymentType.WintSalaryEntry: {
        return { ...payments, wintSalaryEntries: value };
      }
      default: {
        return payments;
      }
    }
  },

  calculateBankIdTotal: (payments: WintSuperkollAPIModel.PaymentSigningInformation) => {
    let total = 0;
    EnumHelper.getNumericItemValues(PaymentEnums.BankIdPaymentType).forEach((key) => {
      // eslint-disable-next-line eqeqeq --- comparing different types
      if (key == PaymentEnums.BankIdPaymentType.IncomingInvoice) {
        total += payments.incomingInvoices?.map((i) => i.totalAmount ?? 0).reduce((a, b) => a + b, 0) ?? 0;
      } else {
        total +=
          (PaymentHelper.getBankIdPaymentsByType(key, payments) as any[])
            ?.map((p) => p.amount ?? 0)
            .reduce((a, b) => a + b, 0) ?? 0;
      }
    });
    return total;
  },

  getTotalBankIdPaymentsCount: (payments: WintSuperkollAPIModel.PaymentSigningInformation) => {
    return (
      (payments.incomingInvoices?.length ?? 0) +
      (payments.receipts?.length ?? 0) +
      (payments.manualPayouts?.length ?? 0) +
      (payments.salaryPayoutFileRows?.length ?? 0) +
      (payments.personSalaryEntries?.length ?? 0) +
      (payments.wintSalaryEntries?.length ?? 0)
    );
  },
};

export default PaymentHelper;
